import { Card, CardBody } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { VideoInsight } from "@/api/studio/insights/types/insight.type";
import { BenchmarkComparison } from "@/api/studio/library/types/benchmark.type";
import { getVideoThumbnailUrl } from "@/api/renderer/api";
import { VideoBenchmarkPill } from "@/features/reports/components/video-benchmark-pill";

/* Prop Types */
interface VideoReportCardProps {
  insight: VideoInsight;
  isTopScore: boolean;
  projectId: string;
  studyId: string;
  benchmarkComparison?: BenchmarkComparison;
  onClick?: () => void;
}

/**
 * VideoReportCard component
 *
 * Renders a single video's report card: thumbnail, asset title, the Creative Impact score as a
 * progress bar + percentage, an optional benchmark comparison pill, and Attention / Emotion / Memory rows.
 */
export const VideoReportCard = ({ insight, isTopScore, projectId, studyId, benchmarkComparison, onClick }: VideoReportCardProps) => {
  /* Consts */
  const { creative } = insight;
  // Emotion and memory are null until the pipeline has produced them for the video
  const metricRows = [
    { label: "Attention", score: creative.attention.score },
    { label: "Emotion", score: creative.emotion?.score ?? null },
    { label: "Memory", score: creative.memory?.score ?? null },
  ];

  /* Render */
  return (
    <Card imageUrl={getVideoThumbnailUrl({ projectId, studyId, assetKey: insight.key })} imgFit="cover" winner={isTopScore} hideWinnerBorder size="xlarge" onClick={onClick}>
      <CardBody className="flex flex-col gap-4 px-6 py-5">
        <p className="font-semibold truncate text-sm text-base-content/60 pb-2">{insight.assetName}</p>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-base font-medium">
            <h3>Creative Impact</h3>
            <div className="flex items-center gap-2">
              <Progress value={Math.round(creative.score)} className="w-[69px] h-1.5" />
              <h2>{Math.round(creative.score)}%</h2>
            </div>
          </div>

          {benchmarkComparison && (
            <div className="flex justify-end">
              <VideoBenchmarkPill comparison={benchmarkComparison} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {metricRows.map(({ label, score }) => (
            <div key={label} className="flex items-center justify-between text-sm font-semibold">
              <span className="text-base-content/70">{label}</span>
              <div className="flex items-center gap-2">
                <Progress value={score === null ? 0 : Math.round(score)} className="w-[69px] h-1.5" />
                <span>{score === null ? "-" : `${Math.round(score)}%`}</span>
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};
